/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Popover, Radio } from '@coursera/cds-core';
import { FilterIcon } from '@coursera/cds-icons';

import FilterPill from 'bundles/enterprise-ui/components/FilterPill';

import _t from 'i18n!nls/enterprise-ui';

export type SortSingleSelectOption<T extends string> = {
  /** The value reported via {@link SortSingleSelectProps.onChange} when this option is applied. */
  value: T;
  /** Human-readable text shown for this option in the radio list. */
  label: string;
};

export type SortSingleSelectProps<T extends string> = {
  /** The options rendered as single-select radios inside the popover. */
  options: ReadonlyArray<SortSingleSelectOption<T>>;
  /** The currently applied value. */
  value: T;
  /** Invoked with the chosen value when the user presses Apply (or Clear all). */
  onChange: (value: T) => void;
  /**
   * Trigger label.
   * @defaultValue "Sort by"
   */
  label?: string;
  /**
   * Accessible label for the trigger button.
   * @defaultValue {@link SortSingleSelectProps.label}
   */
  'aria-label'?: string;
  /**
   * Value applied when the user presses "Clear all".
   * @defaultValue the first option's value
   */
  defaultValue?: T;
  /**
   * Leading icon shown in the trigger pill.
   * @defaultValue `<FilterIcon />`
   */
  leadingIcon?: React.ReactNode;
  'data-testid'?: string;
};

const styles = {
  body: css`
    min-width: 240px;
  `,
  options: css`
    padding: var(--cds-spacing-200) 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);

    label {
      margin: 0;
    }
  `,
  footer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    padding: var(--cds-spacing-200) 0 var(--cds-spacing-100);
    border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
};

/**
 * A generic "sort by" control: a {@link FilterPill} trigger that opens a popover with a
 * single-select radio list and an Apply / Clear all footer. Selection is deferred — nothing
 * is reported until the user presses Apply (or Clear all, which reverts to
 * {@link SortSingleSelectProps.defaultValue}).
 *
 * Domain-specific value mapping (e.g. `{ key, direction }` sort descriptors) stays in the
 * caller; this component only deals in opaque string values.
 *
 * @example
 * ```tsx
 * <SortSingleSelect
 *   options={[
 *     { value: 'newest', label: _t('Newest') },
 *     { value: 'oldest', label: _t('Oldest') },
 *   ]}
 *   value={value}
 *   onChange={setValue}
 *   defaultValue="newest"
 * />
 * ```
 */
export function SortSingleSelect<T extends string>({
  options,
  value,
  onChange,
  label,
  'aria-label': ariaLabel,
  defaultValue,
  leadingIcon,
  'data-testid': dataTestId,
}: SortSingleSelectProps<T>) {
  const [anchor, setAnchor] = React.useState<HTMLButtonElement | null>(null);
  const [draft, setDraft] = React.useState<T>(value);
  const radioGroupName = React.useId();

  const triggerLabel = label ?? _t('Sort by');
  const clearValue: T | undefined = defaultValue ?? options[0]?.value;

  const open = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraft(value);
    setAnchor(event.currentTarget);
  };
  const close = () => setAnchor(null);

  return (
    <React.Fragment>
      <FilterPill
        label={triggerLabel}
        aria-label={ariaLabel ?? triggerLabel}
        leadingIcon={leadingIcon ?? <FilterIcon />}
        open={Boolean(anchor)}
        onClick={open}
        data-testid={dataTestId}
      />
      <Popover open={Boolean(anchor)} anchorElement={anchor} onClose={close}>
        <Popover.Body component="div" css={styles.body}>
          <div css={styles.options}>
            {options.map((option) => (
              <Radio
                key={option.value}
                name={radioGroupName}
                label={option.label}
                value={option.value}
                checked={draft === option.value}
                onChange={() => setDraft(option.value)}
              />
            ))}
          </div>
          <div css={styles.footer}>
            <Button
              size="small"
              variant="primary"
              onClick={() => {
                onChange(draft);
                close();
              }}
            >
              {_t('Apply')}
            </Button>
            <Button
              size="small"
              variant="ghost"
              onClick={() => {
                if (clearValue !== undefined) {
                  setDraft(clearValue);
                  onChange(clearValue);
                }
                close();
              }}
            >
              {_t('Clear all')}
            </Button>
          </div>
        </Popover.Body>
      </Popover>
    </React.Fragment>
  );
}

export default SortSingleSelect;
